import {
  Color,
  Points,
  Ray,
  Raycaster,
  Vector3
} from "three";
import { ORIGINAL } from "../config/ViewerConfig";
import { Viewer } from "../viewer";
import { triangulate } from "./Delaunay";
import { refinePointPCA } from "./PcaRefine";
import { IPointCloudTile } from "./PointCloudTile";

// Screen-space sampling window (pixels, square) read around the click for
// picking + PCA refine. Bigger = more tolerant clicks and smoother refine,
// but slower readPixels and risk of grabbing neighboring surfaces.
const PICK_WINDOW_SIZE = 32;

export class PointCloudBVH {
  private _viewer: Viewer;
  private _rootTile: IPointCloudTile;
  private _flatMapUrlToTile: Map<string, IPointCloudTile>; // tile url to all available tileMap
  // private _tileMapPickIndexToUrl: Map<number, string>; // tile url to all available tileMap
  private _offset: Vector3;
  public onOffsetChanged = [] as (() => void)[];


  get offset(): Vector3 {
    return this._offset;
  }

  set offset(o: Vector3) {
    this._offset.copy(o);
    this.onOffsetChanged.forEach((f) => f());
  }

  get rootTile(): IPointCloudTile {
    return this._rootTile;
  }

  getFlatMapUrlToTile() {
    return this._flatMapUrlToTile;
  }

  setRootTile(tile: IPointCloudTile) {
    this._rootTile = tile;
    this._flatMapUrlToTile.clear();

    // fillup flatmap
    const fillFlatMap = (_t: IPointCloudTile) => {
      for (const c of _t.children) {
        fillFlatMap(c);
      }
      this._flatMapUrlToTile.set(_t.url, _t);
    };
    fillFlatMap(this._rootTile);
  }

  setRootTileForOriginal(tile: IPointCloudTile) {
    this._rootTile = tile;
    this._flatMapUrlToTile.clear();

    // fillup flatmap
    this._flatMapUrlToTile.set(ORIGINAL, tile);
  }

  constructor(viewer: Viewer) {
    this._viewer = viewer;
    this._flatMapUrlToTile = new Map();
    this._offset = new Vector3();
  }

  public clear() {
    this.disposeHierchy(this._rootTile);
    this._rootTile = undefined;
    this._flatMapUrlToTile.clear();
    this._offset.set(0, 0, 0);
  }

  public disposeHierchy(node: IPointCloudTile) {
    if (!node) return;
    node.pointcloud?.dispose();
    for (const child of node.children) {
      this.disposeHierchy(child);
    }
  }

  public intersectPointCloud(
    tileMap: Map<number, string>,
    gl: WebGLRenderingContext,
    pickCoord: { x: number; y: number },
    refine = false
  ): Vector3 | undefined {
    for (const url of tileMap.values()) {
      this._flatMapUrlToTile.get(url).pointcloud.setPickMaterial(true);
    }

    // Sampling window around the click ("dab an area and sample inside").
    // One readPixels call costs the same as reading a single pixel; the
    // window is a MAXIMUM — the nearest hit always wins, so far pixels
    // only matter when everything closer is empty.
    const pickWindowSize = PICK_WINDOW_SIZE;
    const half = Math.floor(pickWindowSize / 2);
    const x0 = Math.max(
      0,
      Math.min(
        Math.round(pickCoord.x) - half,
        gl.drawingBufferWidth - pickWindowSize
      )
    );
    const y0 = Math.max(
      0,
      Math.min(
        Math.round(pickCoord.y) - half,
        gl.drawingBufferHeight - pickWindowSize
      )
    );

    const pixels = new Uint8Array(pickWindowSize * pickWindowSize * 4);

    this._viewer.singleRender();

    gl.readPixels(
      x0,
      y0,
      pickWindowSize,
      pickWindowSize,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      pixels
    );

    for (const url of tileMap.values()) {
      this._flatMapUrlToTile.get(url).pointcloud.setPickMaterial(false);
    }

    this._viewer.singleRender();

    try {
      const cx = Math.round(pickCoord.x) - x0;
      const cy = Math.round(pickCoord.y) - y0;
      // float cursor position inside the window — barycentric interpolation
      // lives on the fractional part (where the cursor sits WITHIN a cell)
      const fx = pickCoord.x - x0;
      const fy = pickCoord.y - y0;

      let nearest: Vector3 | undefined;
      let nearestD2 = Infinity;
      const samples: Vector3[] = [];
      // remember which sample sits in which pixel — feeds the Delaunay
      // mesh (vertex = point, screen position = its pixel footprint)
      const grid: (Vector3 | undefined)[] = new Array(
        pickWindowSize * pickWindowSize
      );

      for (let py = 0; py < pickWindowSize; py++) {
        for (let px = 0; px < pickWindowSize; px++) {
          const o = (py * pickWindowSize + px) * 4;
          // encoded indices start at 1 → RGB all zero = background
          if (
            pixels[o] === 0 &&
            pixels[o + 1] === 0 &&
            pixels[o + 2] === 0
          ) {
            continue;
          }
          const url = tileMap.get(pixels[o + 3]);
          if (url === undefined) continue; // pixel not from a point cloud
          const tile = this._flatMapUrlToTile.get(url);
          if (!tile || !tile.pointcloud) continue;
          const index =
            (pixels[o] | (pixels[o + 1] << 8) | (pixels[o + 2] << 16)) - 1;
          const position = tile.pointcloud
            .getMesh()
            .geometry.getAttribute("position").array;
          if (index < 0 || 3 * index + 2 >= position.length) continue;
          const point = new Vector3(
            position[3 * index],
            position[3 * index + 1],
            position[3 * index + 2]
          );
          samples.push(point);
          grid[py * pickWindowSize + px] = point;
          const dx = px - cx;
          const dy = py - cy;
          const d2 = dx * dx + dy * dy;
          if (d2 < nearestD2) {
            nearestD2 = d2;
            nearest = point;
          }
        }
      }

      if (!nearest) return undefined; // whole window empty — never guess

      // Mesh + barycentric anchor: triangulate the window points with
      // Delaunay (screen space), locate the triangle CONTAINING the click,
      // then interpolate the aimed position with barycentric coordinates —
      // instead of snapping to the nearest scanned point (which quantizes
      // the pick to the point spacing). Tier ladder:
      //   0–1 points → undefined (nearest keeps the old behaviour)
      //   2 points   → linear interpolation along the segment, by cursor
      //   ≥3 points  → Delaunay mesh → containing triangle → barycentric
      // Outside the mesh hull / degenerate / depth-edge guard failure →
      // fall back to `nearest` — never worse than the old behaviour.
      const baryAnchor = (): Vector3 | undefined => {
        interface IVert {
          p: Vector3;
          sx: number;
          sy: number;
          d2: number;
        }
        // Merge pixels per REAL 3D point first: with point size > 1px one
        // point covers many pixels; un-merged, the mesh is full of
        // duplicate vertices (3D edge length 0) and the guards reject
        // every click near a dot. One point = one vertex, screen position
        // = centroid of the dot's pixels.
        const groups = new Map<
          string,
          { p: Vector3; sxSum: number; sySum: number; n: number }
        >();
        for (let b = 0; b < pickWindowSize; b++) {
          for (let a = 0; a < pickWindowSize; a++) {
            const p = grid[b * pickWindowSize + a];
            if (!p) continue;
            const key = `${p.x}|${p.y}|${p.z}`;
            const g = groups.get(key);
            if (g) {
              g.sxSum += a + 0.5;
              g.sySum += b + 0.5;
              g.n++;
            } else {
              groups.set(key, { p, sxSum: a + 0.5, sySum: b + 0.5, n: 1 });
            }
          }
        }
        const verts: IVert[] = [];
        groups.forEach((g) => {
          const sx = g.sxSum / g.n;
          const sy = g.sySum / g.n;
          const dx = sx - fx;
          const dy = sy - fy;
          verts.push({ p: g.p, sx, sy, d2: dx * dx + dy * dy });
        });

        if (verts.length < 2) return undefined; // tier 0–1 → nearest

        // depth-edge guard: on ONE surface the "3D length per screen px"
        // ratio of nearby edges stays consistent; an edge crossing a depth
        // discontinuity has a wildly larger ratio → refuse to interpolate
        const edgeRatio = (P: IVert, Q: IVert): number => {
          const s = Math.hypot(P.sx - Q.sx, P.sy - Q.sy);
          return s > 1e-9 ? P.p.distanceTo(Q.p) / s : Infinity;
        };

        if (verts.length === 2) {
          // tier 2: interpolate along the segment, weighted by the cursor
          // (aim mid → midpoint, aim an end → that end)
          const [A, B] = verts;
          const vx = B.sx - A.sx;
          const vy = B.sy - A.sy;
          const len2 = vx * vx + vy * vy;
          if (len2 <= 1e-12) return undefined;
          let t = ((fx - A.sx) * vx + (fy - A.sy) * vy) / len2;
          t = Math.max(0, Math.min(1, t));
          return new Vector3(
            A.p.x + (B.p.x - A.p.x) * t,
            A.p.y + (B.p.y - A.p.y) * t,
            A.p.z + (B.p.z - A.p.z) * t
          );
        }

        // tier ≥3: Delaunay mesh over the (merged) window points. Cap the
        // input on huge windows — the containing triangle is certainly
        // among the points nearest to the cursor
        let use = verts;
        const CAP = 500;
        if (verts.length > CAP) {
          use = verts.slice().sort((u, v) => u.d2 - v.d2).slice(0, CAP);
        }
        const tris = triangulate(use.map((v) => ({ x: v.sx, y: v.sy })));

        // point location: the cursor lies in exactly one triangle — the
        // one whose 3 barycentric weights are all non-negative; those same
        // weights are then the interpolation coefficients
        for (const [ia, ib, ic] of tris) {
          const A = use[ia];
          const B = use[ib];
          const C = use[ic];
          const denom =
            (B.sy - C.sy) * (A.sx - C.sx) + (C.sx - B.sx) * (A.sy - C.sy);
          if (Math.abs(denom) <= 1e-12) continue; // degenerate sliver
          const wa =
            ((B.sy - C.sy) * (fx - C.sx) + (C.sx - B.sx) * (fy - C.sy)) /
            denom;
          const wb =
            ((C.sy - A.sy) * (fx - C.sx) + (A.sx - C.sx) * (fy - C.sy)) /
            denom;
          const wc = 1 - wa - wb;
          if (wa < -1e-6 || wb < -1e-6 || wc < -1e-6) continue; // not this one

          // containing triangle found — depth-edge guard before trusting it
          const r1 = edgeRatio(A, B);
          const r2 = edgeRatio(B, C);
          const r3 = edgeRatio(C, A);
          const mx = Math.max(r1, r2, r3);
          const mn = Math.min(r1, r2, r3);
          if (!isFinite(mx) || mn <= 1e-12 || mx > 4 * mn) return undefined;

          return new Vector3(
            A.p.x * wa + B.p.x * wb + C.p.x * wc,
            A.p.y * wa + B.p.y * wb + C.p.y * wc,
            A.p.z * wa + B.p.z * wb + C.p.z * wc
          );
        }

        return undefined; // cursor outside the mesh hull → nearest
      };

      // refine=true: anchor = exact aimed position on the local mesh
      // (barycentric decides the LATERAL position, the MAD-filtered PCA
      // plane projection still cleans the NORMAL direction afterwards).
      // refine=false callers keep the old nearest-point behaviour intact.
      return refine ? refinePointPCA(samples, baryAnchor() ?? nearest) : nearest;
    } catch (err) {
      return undefined;
    }
  }

  public intersectPointCloudFromRay(raycaster: Raycaster): Vector3 | undefined {
    //Todo need to get the nearest point
    //sort by distance
    if (!this._rootTile) {
      console.warn("Pointcloud not found");
      return;
    }

    const possiblePointClouds: Points[] = [];

    const _intersectsPointCloud = (_tile: IPointCloudTile, ray: Ray): void => {
      if (_tile.intersects(ray)) {
        if (_tile.pointcloud) {
          possiblePointClouds.push(_tile.pointcloud.getMesh());
        }
        for (const child of _tile.children) {
          _intersectsPointCloud(child, ray);
        }
      }
    };

    //collect the possible pointclouds
    for (const c of this._rootTile.children) {
      _intersectsPointCloud(c, raycaster.ray);
    }

    if (this._rootTile.pointcloud) {
      possiblePointClouds.push(this._rootTile.pointcloud.getMesh());
    }

    const hits = raycaster.intersectObjects(possiblePointClouds);

    if (hits.length > 0) {
      return hits[0].point;
    }

    return;
  }

  setVisibility(visibility: boolean) {
    this._rootTile?.setVisibility(visibility);
  }

  public setPointSize(size: number) {
    this._rootTile?.setPointSize(size);
  }

  public removeSelectedPoints(selectionColor: Color) {
    this._rootTile?.removeSelectedPoints(selectionColor);
  }

  public resetPointCloudColor() {
    this._rootTile?.resetColor();
  }
}
